import { useState, useEffect, useCallback } from 'react'
import { View, Text, Platform } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import i18n, { changeLanguageLocally } from '@/lib/i18n/config'
import { selection } from '@/lib/utils/haptics'
import {
  Fingerprint,
  Palette,
  ChevronRight,
  Globe,
  UserMinus,
  Bell,
  CreditCard,
  Shield,
  Download,
} from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ToggleTrack } from '@/components/ui/ToggleTrack'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useProfile } from '@/lib/hooks/use-profile'
import { useSemanticColors, RADIUS, SPACING, INTERACTION } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { BiometricStore } from '@/lib/storage/secure-storage'
import { toast } from '@/lib/toast'
import { captureError } from '@/lib/utils/observability'

/**
 * One height for every row in a settings card.
 *
 * The rows here were assembled from three different recipes and each fell out
 * at a different height: the icon+label rows measured 48pt (14pt of padding
 * around a 20pt icon), while the "Idioma" row is sized by the 44pt touch
 * target its ES/EN chips are required to carry, so it measured 52. Four rows
 * in one card broke rhythm at the last divider. Sizing all of them from the
 * touch target — the one number that cannot move — makes the dividers evenly
 * spaced and keeps every row comfortably tappable.
 */
const ROW_HEIGHT = INTERACTION.minTouchTarget + SPACING.sm

function SettingsSection({ children }: { children: React.ReactNode }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <View
      style={{
        marginHorizontal: SPACING.md,
        marginTop: SPACING.md,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        ...WEB_SHADOWS.sm,
      }}
    >
      {children}
    </View>
  )
}

function SectionTitle({ title }: { title: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Text
      style={{
        fontSize: 12,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        fontFamily: FONTS.body,
        paddingHorizontal: SPACING.md,
        marginTop: SPACING.lg,
        marginBottom: SPACING.xs,
      }}
    >
      {title}
    </Text>
  )
}

function SettingsRow({
  icon: Icon,
  label,
  value,
  onPress,
  colors,
  testID,
}: {
  icon: typeof Globe
  label: string
  value?: string
  onPress: () => void
  colors: ReturnType<typeof useSemanticColors>
  testID?: string
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      testID={testID}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: ROW_HEIGHT,
        paddingVertical: SPACING.xs,
        paddingHorizontal: SPACING.md,
        opacity: pressed ? 0.72 : 1,
      })}
    >
      <Icon size={20} color={colors.textSecondary} strokeWidth={1.8} />
      <Text
        style={{
          flex: 1,
          marginLeft: SPACING.md,
          fontSize: 16,
          color: colors.textPrimary,
          fontFamily: FONTS.body,
        }}
      >
        {label}
      </Text>
      {value ? (
        <Text
          style={{
            fontSize: 14,
            color: colors.textSecondary,
            marginRight: SPACING.sm,
            fontFamily: FONTS.body,
          }}
        >
          {value}
        </Text>
      ) : null}
      <ChevronRight size={16} color={colors.textSecondary} strokeWidth={2} />
    </Pressable>
  )
}

function Divider({ colors }: { colors: ReturnType<typeof useSemanticColors> }) {
  return (
    <View style={{ height: 1, backgroundColor: colors.border, marginHorizontal: SPACING.md }} />
  )
}

/**
 * One chip, one shape, both themes.
 *
 * The unselected chip used to carry a 1px border AND a `surfaceSubtle` fill,
 * and each theme showed only one of the two: in light the fill (#F0EFF4) read
 * as a solid grey square and the #E5E5EA border vanished into it; in dark the
 * fill (4% white) vanished into the card and only the border was left, so the
 * same control in the same state read as filled in one theme and outlined in
 * the other. It is filled and borderless in both now — one step off the card
 * it sits on, which is `surfaceSubtle` in light and `surfaceRaised` in dark.
 */
function LanguageChip({
  label,
  accessibilityLabel,
  selected,
  onPress,
  testID,
}: {
  label: string
  accessibilityLabel: string
  selected: boolean
  onPress: () => void
  testID: string
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      style={{
        minWidth: INTERACTION.minTouchTarget,
        minHeight: INTERACTION.minTouchTarget,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: SPACING.sm + SPACING.xs,
        borderRadius: RADIUS.sm,
        backgroundColor: selected
          ? COLORS.coral.dark
          : isDark
            ? colors.surfaceRaised
            : colors.surfaceSubtle,
      }}
    >
      <Text
        style={{
          fontSize: 13,
          // Fixed white on the fixed coral fill — the one hex CLAUDE.md allows.
          color: selected ? '#FFFFFF' : colors.textSecondary,
          fontFamily: FONTS.body,
        }}
      >
        {label}
      </Text>
    </Pressable>
  )
}

export default function SettingsScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { updateProfile } = useProfile()
  const [biometricEnabled, setBiometricEnabled] = useState(false)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  const [currentLang, setCurrentLang] = useState(i18n.language)

  useEffect(() => {
    ;(async () => {
      if (Platform.OS === 'web') return
      const LocalAuth = await import('expo-local-authentication')
      const hasHardware = await LocalAuth.hasHardwareAsync()
      const isEnrolled = await LocalAuth.isEnrolledAsync()
      setBiometricAvailable(hasHardware && isEnrolled)
      const enabled = await BiometricStore.isEnabled()
      setBiometricEnabled(enabled)
    })()
  }, [])

  const handleBiometricToggle = useCallback(
    async (value: boolean) => {
      setBiometricEnabled(value)
      try {
        await BiometricStore.setEnabled(value)
        if (value) await BiometricStore.setPrompted()
        void selection()
      } catch (err) {
        // The switch springing back with no message reads as a bug in the
        // switch. Every other mutation on this surface toasts on failure —
        // match them.
        setBiometricEnabled(!value)
        toast.error(t('settings.biometric_toggle_failed'))
        captureError(err, { screen: 'profile/settings', action: 'biometric-toggle' })
      }
    },
    [t],
  )

  const switchLanguage = useCallback(
    async (lang: 'es' | 'en') => {
      await changeLanguageLocally(lang)
      setCurrentLang(lang)
      try {
        await updateProfile({ preferredLocale: lang })
      } catch (err) {
        if (__DEV__) console.error('Profile locale update failed', err)
      }
      void selection()
    },
    [updateProfile],
  )

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('profile.settings')} />
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.lg }}
      >
        {/* Security */}
        {biometricAvailable && (
          <>
            <SectionTitle title={t('common.security')} />
            <SettingsSection>
              {/* The row is the switch, as on the notification preferences: RN's
                  Switch is a 40x20 <input> on web and the only thing that took
                  the tap, inside a row twice its height. */}
              <Pressable
                onPress={() => handleBiometricToggle(!biometricEnabled)}
                accessibilityRole="switch"
                accessibilityState={{ checked: biometricEnabled }}
                accessibilityLabel={t('settings.biometric_title')}
                testID="settings.biometric-toggle"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  minHeight: ROW_HEIGHT,
                  paddingVertical: SPACING.md - RADIUS.xs,
                  paddingHorizontal: SPACING.md,
                }}
              >
                <Fingerprint size={20} color={colors.textSecondary} strokeWidth={1.8} />
                <View style={{ flex: 1, marginLeft: SPACING.md }}>
                  <Text style={{ fontSize: 16, color: colors.textPrimary, fontFamily: FONTS.body }}>
                    {t('settings.biometric_title')}
                  </Text>
                  <Text
                    style={{
                      fontSize: 13,
                      color: colors.textSecondary,
                      marginTop: 2,
                      fontFamily: FONTS.body,
                    }}
                  >
                    {t('settings.biometric_desc')}
                  </Text>
                </View>
                <ToggleTrack value={biometricEnabled} isDark={isDark} />
              </Pressable>
            </SettingsSection>
          </>
        )}

        {/* Preferences.
            This screen used to hold three rows and stop at 42% of the display,
            with the settings that belong to it — notifications, payment
            methods, privacy, data export — reachable only from the Perfil
            list, which is the identity surface. Everything that configures the
            app now lives here, in the screen named after it. */}
        <SectionTitle title={t('common.preferences')} />
        <SettingsSection>
          <SettingsRow
            icon={Palette}
            label={t('settings.appearance.title')}
            onPress={() => router.push('/profile/appearance')}
            colors={colors}
            testID="settings.appearance"
          />
          <Divider colors={colors} />
          <SettingsRow
            icon={Bell}
            label={t('notification_prefs.title')}
            onPress={() => router.push('/profile/notifications-prefs' as never)}
            colors={colors}
            testID="settings.notification-prefs"
          />
          <Divider colors={colors} />
          <SettingsRow
            icon={CreditCard}
            label={t('profile.payment_methods')}
            onPress={() => router.push('/profile/payment-methods')}
            colors={colors}
            testID="settings.payment-methods"
          />
          <Divider colors={colors} />
          {/* The chips carry the 44pt touch target themselves, so SPACING.xs
              of padding is all this row needs to reach ROW_HEIGHT — the same
              52pt its three siblings above now stand at. It used to take the
              14pt they used and came out 75pt tall against their 48. */}
          <View
            accessibilityRole="radiogroup"
            accessibilityLabel={t('profile.language')}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              minHeight: ROW_HEIGHT,
              paddingVertical: SPACING.xs,
              paddingHorizontal: SPACING.md,
            }}
          >
            <Globe size={20} color={colors.textSecondary} strokeWidth={1.8} />
            <Text
              style={{
                flex: 1,
                marginLeft: SPACING.md,
                fontSize: 16,
                color: colors.textPrimary,
                fontFamily: FONTS.body,
              }}
            >
              {t('profile.language')}
            </Text>
            <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
              <LanguageChip
                label="ES"
                accessibilityLabel="Español"
                selected={currentLang === 'es'}
                onPress={() => switchLanguage('es')}
                testID="settings.lang-es"
              />
              <LanguageChip
                label="EN"
                accessibilityLabel="English"
                selected={currentLang === 'en'}
                onPress={() => switchLanguage('en')}
                testID="settings.lang-en"
              />
            </View>
          </View>
        </SettingsSection>

        {/* Privacy. Deliberately NOT titled "Seguridad": the biometric group
            above already carries that name, and two identical headers on one
            screen make the second group unreadable. i18next returns the key
            when a string is missing — never null — so the `?? 'Seguridad'`
            fallbacks that used to sit here could not fire. */}
        <SectionTitle title={t('settings.safety_section')} />
        <SettingsSection>
          <SettingsRow
            icon={Shield}
            label={t('profile.privacy')}
            onPress={() => router.push('/profile/privacy')}
            colors={colors}
            testID="settings.privacy"
          />
          <Divider colors={colors} />
          <SettingsRow
            icon={Download}
            label={t('profile.data_export')}
            onPress={() => router.push('/profile/data-export' as never)}
            colors={colors}
            testID="settings.data-export"
          />
          <Divider colors={colors} />
          <SettingsRow
            icon={UserMinus}
            label={t('settings.blocked_users')}
            onPress={() => router.push('/profile/blocked-users' as never)}
            colors={colors}
            testID="settings.blocked-users"
          />
        </SettingsSection>
      </ScrollView>
    </ScreenContainer>
  )
}
